import type { QueryKey } from '@tanstack/react-query';
import { NEW_ITEMS_CHECK_INTERVAL_MS } from '@/utils/new-items';

export interface NewItemsCheckInput<TPage, TKey extends QueryKey> {
  /** The check's own key: never the list's, so the check never changes the list. */
  queryKey: TKey;
  /** Reads the first page, with the same params as the list. */
  queryFn: (context: { signal: AbortSignal }) => Promise<TPage>;
  /** False stops the check, for example while the list is still loading. */
  enabled: boolean;
  /** The list's first page and when it was fetched, so the page loads once. */
  seed: TPage | undefined;
  seedUpdatedAt: number;
}

/**
 * Query options for a "load new" check (see `utils/new-items.ts`): read the first page every 15s.
 *
 * - `initialData` applies whenever the cache entry has no data yet (TanStack Query v5
 *   `Query.setOptions`), so the check starts from the list's first page and waits one interval,
 *   even when the entry was created earlier while disabled.
 * - React Query pauses interval polling while the browser tab is hidden
 *   (`refetchIntervalInBackground` is off by default).
 * - The app turns focus refetch off globally; the check turns it back on, so coming back to the tab
 *   checks at once if the last check is 15s old.
 */
export function newItemsCheckOptions<TPage, TKey extends QueryKey>({
  queryKey,
  queryFn,
  enabled,
  seed,
  seedUpdatedAt,
}: NewItemsCheckInput<TPage, TKey>) {
  return {
    queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => queryFn({ signal }),
    initialData: seed,
    initialDataUpdatedAt: seedUpdatedAt,
    staleTime: NEW_ITEMS_CHECK_INTERVAL_MS,
    refetchInterval: NEW_ITEMS_CHECK_INTERVAL_MS,
    refetchOnWindowFocus: true,
    enabled,
  };
}
